import React, { useState } from 'react';
import { X, Loader2, Wand2, Copy, FileText, Download, ExternalLink } from 'lucide-react';
import { generateTaskDescription } from '../lib/openai';
import { toast } from '../lib/toast';
import { supabase } from '../lib/supabase';
import { jsPDF } from 'jspdf';

interface MeetingSummarizerModalProps {
  onClose: () => void;
  navigate: (to: string) => void;
}

export default function MeetingSummarizerModal({ onClose, navigate }: MeetingSummarizerModalProps) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [summaryGenerated, setSummaryGenerated] = useState(false);
  const [savedOutput, setSavedOutput] = useState<{ id: string; saved: boolean } | null>(null);
  const [savingOutput, setSavingOutput] = useState(false);
  const [showFullResponse, setShowFullResponse] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) {
      toast.error('Please enter meeting notes');
      return;
    }

    setLoading(true);
    try {
      const prompt = `Please summarize the following meeting notes in plain text format (no HTML):

${input}

Please structure the summary with the following sections:
1. Meeting Overview
2. Key Discussion Points
3. Action Items
4. Decisions Made
5. Next Steps

Use plain text formatting with line breaks and dashes for bullet points.`;

      const response = await generateTaskDescription(prompt, 'task');
      
      // Remove any HTML tags from the response
      const cleanResponse = response.replace(/<[^>]*>/g, '');
      setResult(cleanResponse);
      setSummaryGenerated(true);
      toast.success('Meeting summary generated successfully');

      // Prepare for saving but don't save automatically
      setSavedOutput({
        id: crypto.randomUUID(),
        saved: false
      });
    } catch (error) {
      console.error('Error generating summary:', error);
      toast.error('Failed to generate summary');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOutput = async () => {
    if (!result || !savedOutput || savedOutput.saved) return;
    
    setSavingOutput(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to save outputs');
        setSavingOutput(false);
        return;
      }
      
      // Get the agent ID for Meeting Summarizer
      const { data: agentData, error: fetchError } = await supabase
        .from('ai_agents')
        .select('id')
        .eq('id', '00000000-0000-0000-0000-000000000005')
        .limit(1)
        .maybeSingle();
      
      if (fetchError) {
        console.error('Error fetching agent:', fetchError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      let agentId;
      
      if (!agentData) {
        // Try to find by name
        try {
          const { data: agentByName, error: nameError } = await supabase
            .from('ai_agents')
            .select('id')
            .eq('name', 'Meeting Summarizer')
            .limit(1)
            .maybeSingle();
            
          if (nameError) throw nameError;
          
          if (!agentByName) {
            // Create the Meeting Summarizer agent
            const { data: newAgent, error: createError } = await supabase
              .from('ai_agents')
              .insert({
                name: 'Meeting Summarizer',
                user_id: user.id,
                goals: 'Summarize meeting notes into structured, actionable summaries',
                system_prompt: 'You are a meeting summarizer that helps extract key information from meeting notes and organize them into clear, structured summaries.',
                instructions: 'Analyze meeting notes and create summaries that include: Meeting Overview, Key Discussion Points, Action Items, Decisions Made, and Next Steps.',
                is_custom: false,
                agent_type: 'text'
              })
              .select('id')
              .single();
              
            if (createError) throw createError;
            agentId = newAgent.id;
          } else {
            agentId = agentByName.id;
          }
        } catch (error) {
          console.error('Error finding or creating agent:', error);
          
          // Use a hardcoded ID as fallback for the Meeting Summarizer
          agentId = '00000000-0000-0000-0000-000000000005';
        }
      } else {
        agentId = agentData.id;
      }
      
      // Save output to ai_agent_outputs table
      const { error: insertError } = await supabase.from('ai_agent_outputs').insert({
        user_id: user.id,
        agent_id: agentId,
        prompt: input,
        output_url: result,
        generation_type: 'text',
        llm_model: 'gpt-3.5-turbo',
        metadata: {},
        agent_type: 'text'
      });
      
      if (insertError) {
        console.error('Error saving output:', insertError);
        toast.error('Failed to save output');
        setSavingOutput(false);
        return;
      }
      
      toast.success('Output saved successfully');
      setSavedOutput({...savedOutput, saved: true});
    } catch (error) {
      console.error('Error saving agent output:', error);
      toast.error('Failed to save output');
    } finally {
      setSavingOutput(false);
    }
  };

  const handleSaveAsNote = async () => {
    if (!result) return;
    
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('notes')
        .insert([{
          title: 'Meeting Summary',
          content: result,
          user_id: user.id,
          category: 'meeting'
        }]);

      if (error) throw error;
      
      toast.success('Meeting summary saved to notes');
      navigate('/notes');
    } catch (error) {
      console.error('Error saving note:', error);
      toast.error('Failed to save meeting summary');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(result || '');
      toast.success('Copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleDownload = () => {
    try {
      const content = `Meeting Summary\n\nOriginal Notes:\n${input}\n\nSummary:\n${result}`;
      const blob = new Blob([content], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'meeting-summary.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading summary:', error);
      toast.error('Failed to download summary');
    }
  };

  const downloadPDF = () => {
    try {
      if (!result) return;
      
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      
      // Add header with title
      pdf.setFillColor(79, 70, 229); // Indigo color
      pdf.rect(0, 0, pageWidth, 40, 'F');
      
      // Add title with white text
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const title = 'Meeting Summary';
      const titleWidth = pdf.getStringUnitWidth(title) * 22 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 25);
      
      // Reset text color for content
      pdf.setTextColor(0, 0, 0);
      
      // Start position after header
      let y = 50;
      
      // Add metadata
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(107, 114, 128);
      pdf.text(`Generated on: ${new Date().toLocaleString()}`, margin, y);
      y += 15;
      
      // Add original notes section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(0, 0, 0);
      pdf.text('Original Meeting Notes:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const wrappedInput = pdf.splitTextToSize(input, pageWidth - (margin * 2));
      pdf.text(wrappedInput, margin, y);
      y += wrappedInput.length * 7 + 15;
      
      // Check if we need a new page
      if (y > pdf.internal.pageSize.height - 60) {
        pdf.addPage();
        y = 20;
      }
      
      // Add summary section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Meeting Summary:', margin, y);
      y += 10;
      
      // Process the summary text by sections
      const sections = result.split(/\n\n+/);
      
      // Add a table of contents
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255); // Light blue background
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Summary Contents:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      // Extract section titles for table of contents
      const sectionTitles = sections.map(section => {
        const lines = section.split('\n');
        return lines[0].trim();
      }).filter(Boolean);
      
      // Add section titles to table of contents
      sectionTitles.forEach((title, index) => {
        pdf.text(`${index + 1}. ${title}`, margin, y);
        y += 7;
      });
      
      y += 10;
      
      sections.forEach(section => {
        // Check if we need a new page
        if (y > pdf.internal.pageSize.height - 40) {
          pdf.addPage();
          y = 20;
        }
        
        const lines = section.split('\n');
        
        // First line of each section is a header
        if (lines.length > 0) {
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          
          // Add background for section headers
          pdf.setFillColor(240, 247, 255);
          pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
          
          pdf.text(lines[0], margin, y);
          y += 10;
          
          // Rest of the lines are content
          if (lines.length > 1) {
            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'normal');
            
            for (let i = 1; i < lines.length; i++) {
              // Check if we need a new page
              if (y > pdf.internal.pageSize.height - 20) {
                pdf.addPage();
                y = 20;
              }
              
              const line = lines[i].trim();
              if (line) {
                // Check if this is a bullet point
                if (line.startsWith('-') || line.startsWith('•')) {
                  const bulletText = line.substring(1).trim();
                  const wrappedBullet = pdf.splitTextToSize(bulletText, pageWidth - (margin * 2) - 10);
                  
                  pdf.text('•', margin, y);
                  pdf.text(wrappedBullet, margin + 5, y);
                  y += wrappedBullet.length * 7;
                } else {
                  const wrappedText = pdf.splitTextToSize(line, pageWidth - (margin * 2));
                  pdf.text(wrappedText, margin, y);
                  y += wrappedText.length * 7;
                }
              }
            }
          }
        }
        
        y += 5; // Add space between sections
      });
      
      // Add footer to all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        pdf.text(`Generated by PM Pro AI | Page ${i} of ${pageCount}`, margin, pdf.internal.pageSize.height - 10);
      }
      
      pdf.save('meeting-summary.pdf');
    } catch (error) {
      console.error('Error creating PDF:', error);
      toast.error('Failed to create PDF');
    }
  };

  const handleDownloadTemplate = () => {
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.width;
      const margin = 20;
      
      // Add header
      pdf.setFillColor(79, 70, 229);
      pdf.rect(0, 0, pageWidth, 40, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      const title = 'Meeting Summary Example';
      const titleWidth = pdf.getStringUnitWidth(title) * 22 / pdf.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      pdf.text(title, titleX, 25);
      
      // Reset text color
      pdf.setTextColor(0, 0, 0);
      
      let y = 50;
      
      // Example Input section
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('User Input Example:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      const exampleInput = 
`Attendees: John (Client), Lisa (Project Manager), Alex (Lead Developer)
Client reviewed the latest website prototype
John liked the new design but requested color adjustments to match branding
He also asked for improved mobile responsiveness, especially on product pages
Lisa assured updates would be completed within a week
Alex mentioned that integrating the requested analytics tool might take extra time
John asked for a demo of the analytics feature in the next meeting
Next meeting scheduled for Wednesday
Lisa to send an updated timeline by Monday
Alex to check feasibility of analytics integration`;
      
      const wrappedInput = pdf.splitTextToSize(exampleInput, pageWidth - (margin * 2));
      pdf.text(wrappedInput, margin, y);
      y += wrappedInput.length * 7 + 15;
      
      // Add new page for example output
      pdf.addPage();
      y = 20;
      
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('AI-Generated Meeting Summary:', margin, y);
      y += 15;
      
      // Meeting Overview section
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Meeting Overview:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Participants: John (Client), Lisa (Project Manager), Alex (Lead Developer)', margin, y);
      y += 7;
      pdf.text('Mood: Positive but with some concerns about branding and mobile responsiveness', margin, y);
      y += 7;
      pdf.text('Next Meeting: Scheduled for Wednesday', margin, y);
      y += 15;
      
      // Key Discussion Points section
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Key Discussion Points:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const discussionPoints = [
        'John provided feedback on the website prototype, appreciating the overall design but requesting branding color adjustments.',
        'He highlighted mobile responsiveness issues on product pages and emphasized the importance of a seamless mobile experience.',
        'Lisa assured that branding and responsiveness updates would be completed within a week.',
        'Alex raised concerns that integrating the requested analytics tool could take additional time.',
        'John requested a demo of the analytics feature in the next meeting.'
      ];
      
      discussionPoints.forEach(point => {
        pdf.text('•', margin, y);
        const wrappedPoint = pdf.splitTextToSize(point, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedPoint, margin + 5, y);
        y += wrappedPoint.length * 7;
      });
      
      y += 10;
      
      // Action Items section
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Action Items:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const actionItems = [
        'Lisa to send an updated timeline to John by Monday.',
        'Alex to assess the technical feasibility of integrating the analytics tool and provide an update.',
        'Development team to implement branding and responsiveness improvements within a week.'
      ];
      
      actionItems.forEach(item => {
        pdf.text('•', margin, y);
        const wrappedItem = pdf.splitTextToSize(item, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedItem, margin + 5, y);
        y += wrappedItem.length * 7;
      });
      
      y += 10;
      
      // Decisions Made section
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Decisions Made:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const decisions = [
        'Proceed with color adjustments and mobile optimizations as a priority.',
        'Conduct a demo of the analytics tool in the next meeting before finalizing integration.'
      ];
      
      decisions.forEach(decision => {
        pdf.text('•', margin, y);
        const wrappedDecision = pdf.splitTextToSize(decision, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedDecision, margin + 5, y);
        y += wrappedDecision.length * 7;
      });
      
      y += 10;
      
      // Next Steps section
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.setFillColor(240, 247, 255);
      pdf.rect(margin - 5, y - 5, pageWidth - (margin * 2) + 10, 12, 'F');
      pdf.text('Next Steps:', margin, y);
      y += 10;
      
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      
      const nextSteps = [
        'Complete requested design updates and ensure mobile responsiveness improvements.',
        'Evaluate the analytics tool integration timeline and discuss feasibility.',
        'Review changes and present the analytics demo in the next client meeting on Wednesday.'
      ];
      
      nextSteps.forEach(step => {
        pdf.text('•', margin, y);
        const wrappedStep = pdf.splitTextToSize(step, pageWidth - (margin * 2) - 10);
        pdf.text(wrappedStep, margin + 5, y);
        y += wrappedStep.length * 7;
      });
      
      y += 10;
      
      // Add question
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'italic');
      pdf.text('Would you like to include automatic tracking of client requests for better follow-up?', margin, y);
      
      // Add footer
      pdf.setFontSize(9);
      pdf.setTextColor(128, 128, 128);
      pdf.text('Generated by PM Pro AI', margin, pdf.internal.pageSize.height - 10);
      
      pdf.save('meeting-summary-example.pdf');
    } catch (error) {
      console.error('Error creating example PDF:', error);
      toast.error('Failed to create example PDF');
    }
  };

  const toggleFullResponse = () => {
    setShowFullResponse(!showFullResponse);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Meeting Summarizer
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-indigo-50 dark:bg-indigo-900/50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-indigo-800 dark:text-indigo-200 mb-2">Instructions</h3>
            <ul className="text-sm text-indigo-700 dark:text-indigo-300 space-y-1">
              <li>• Paste your meeting notes or transcript below</li>
              <li>• The AI will extract key information including:</li>
              <li className="ml-4">- Meeting overview and context</li>
              <li className="ml-4">- Key discussion points</li>
              <li className="ml-4">- Action items and owners</li>
              <li className="ml-4">- Decisions made</li>
              <li className="ml-4">- Next steps</li>
            </ul>
            <div className="mt-4">
              <button
                onClick={handleDownloadTemplate}
                className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
              >
                <Download className="h-4 w-4 mr-1.5" />
                Download Example PDF
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="meeting-notes" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                Meeting Notes
              </label>
              <textarea
                id="meeting-notes"
                rows={6}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                className="w-full h-24 rounded-lg border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                placeholder="Paste your meeting notes here..."
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                    Generating Summary...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-5 w-5 mr-2" />
                    Generate Summary
                  </>
                )}
              </button>
            </div>
          </form>

          {result && (
            <div className={`mt-8 ${showFullResponse ? 'fixed inset-0 z-50 bg-white dark:bg-gray-900 overflow-auto p-6' : ''}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Meeting Summary
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={handleCopy}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-indigo-600 bg-indigo-100 hover:bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-300"
                  >
                    <Copy className="h-4 w-4 mr-1.5" />
                    Copy
                  </button>
                  <div className="flex space-x-2">
                    <button
                      onClick={downloadPDF}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      <FileText className="h-4 w-4 mr-1.5" />
                      Download PDF
                    </button>
                    <button
                      onClick={handleSaveOutput}
                      disabled={savingOutput || (savedOutput && savedOutput.saved)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {savingOutput ? (
                        <>
                          <Loader2 className="animate-spin h-4 w-4 mr-1.5" />
                          Saving...
                        </>
                      ) : savedOutput && savedOutput.saved ? (
                        'Saved'
                      ) : (
                        'Save Output'
                      )}
                    </button>
                  </div>
                  <button
                    onClick={handleSaveAsNote}
                    disabled={saving}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="animate-spin h-4 w-4 mr-1.5" />
                        Saving...
                      </>
                    ) : (
                      'Save as Note'
                    )}
                  </button>
                </div>
              </div>
              {summaryGenerated && (
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg mb-4">
                  <p className="text-sm text-green-700 dark:text-green-300 font-medium">
                    Meeting Summary Generated Successfully! You can now download it as PDF or save it as a note.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}